import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  FormLabel,
  TextField,
  Typography,
  Button,
  FormControl,
  Input,
} from "@mui/material";
import Layout from "../../layout/Layout";
import * as Yup from "yup";
import MenuItem from "@mui/material/MenuItem";
import {
  phoneRegExp,
  insertVendorAPI,
  fetchVendorByIdAPI,
  updateVendorByIdAPI,
} from "../../config";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { fetchStatusByParent } from "../../config";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";

import { emirates } from "../../components/constants";
import { useFormik } from "formik";
import { AlertMessage } from "../../components/AlertMessage";
import { apiData } from "../../services/api_service";
import { Backdrop, CircularProgress } from "@mui/material";

const isKnownEmirateValue = (value) =>
  emirates.some((emirate) => String(emirate.id) === String(value));

const validateUaePhone = (value, isRequired = true) => {
  if (!value) return isRequired ? "Mobile Number is required" : true;

  // Remove spaces/hyphens for clean checking
  const clean = value.replace(/[\s-]/g, "");

  // Accept any country's phone number: optional leading '+', 7-15 digits
  const phoneRegex = /^\+?[0-9]{7,15}$/;
  if (!phoneRegex.test(clean)) {
    return "Enter a valid phone number";
  }

  return true; // ✅ valid
};

const validationSchema = Yup.object().shape({
  company_name: Yup.string().max(150, "Maximum 150 characters"),
  name: Yup.string()
    .required("Supplier name is required")
    .min(3, "Minimum 3 characters")
    .max(100, "Maximum 100 characters")
    .matches(
      /^[a-zA-Z0-9 _-]+$/,
      "Only letters, numbers, spaces, hyphen (-) and underscore (_) are allowed"
    ),
  address: Yup.string()
    .required("Address is required")
    .min(3, "Street Address must be at least 3 characters")
    .max(100, "Street Address cannot be more than 100 characters")
    .matches(
      /^[a-zA-Z0-9 _,-]+$/,
      "Only letters, numbers, spaces, hyphen (-), comma (,) and underscore (_) are allowed"
    ),
  emirate: Yup.string().required("Emirate is required"),

  // po_box: Yup.string().required("P.O.Box is required"),
  phone_number: Yup.string()
    .required("Phone Number is required")
    .test("valid-uae-phone", (value, ctx) => {
      const result = validateUaePhone(value, true);
      return result === true ? true : ctx.createError({ message: result });
    }),

  po_box: Yup.string().matches(/^[0-9]+$/, {
    message: "Only numbers are allowed",
    excludeEmptyString: true,
  }),
  
  vat_no: Yup.string().matches(/^[0-9]+$/, {
    message: "Only numbers are allowed",
    excludeEmptyString: true,
  }),

  email: Yup.string()
    .required("Email is required")
    .matches(
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      "Enter a valid email address"
    ),
});

const Vendor1Add = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const navigate = useNavigate();
  const alertRef = useRef();
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState(false);
  const { vendorid } = useParams();

  const formik = useFormik({
    initialValues: {
      name: "",
      company_name: "",
      address: "",
      emirate: "",
      emirateOption: "",
      po_box: "",
      phone_number: "",
      email: "",
      vat_no: "",
      user_id: "",
      status: 2,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        values.user_id = user.emp_id;

        let result;
        if (vendorid) {
          result = await apiData(
            "put",
            `${updateVendorByIdAPI}/${vendorid}`,
            values
          );
        } else {
          result = await apiData("post", insertVendorAPI, values);
        }

        if (result.success) {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "success",
            })
          );

          setTimeout(() => {
            navigate("/vendor/list"); // redirect to order list after save
          }, 3000);
        } else {
          alertRef.current.triggerAlert(
            JSON.stringify({
              message: result.msg,
              messageType: "error",
            })
          );
        }
      } catch (error) {
        alertRef.current.triggerAlert(
          JSON.stringify({
            message: error.msg,
            messageType: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchStatus = async () => {
      const data = {
        intParentId: 1,
      };

      try {
        const result = await apiData("get", fetchStatusByParent, data);
        if (result.success) {
          setStatus(result.data);
        }
      } catch (error) {
        console.error("Failed to fetch units:", error);
      }
    };

    fetchStatus();
  }, []);

  useEffect(() => {
    if (vendorid) {
      const fetchvendor = async () => {
        try {
          const result = await apiData(
            "get",
            `${fetchVendorByIdAPI}/${vendorid}`
          );
          if (result.success && result.data?.length > 0) {
            const vendor = result.data[0];
            const emirateValue = vendor.emirate || "";
            formik.setValues({
              name: vendor.name || "",
              company_name: vendor.company_name || "",
              address: vendor.address || "",

              emirate: emirateValue,
              emirateOption: isKnownEmirateValue(emirateValue)
                ? emirateValue
                : emirateValue
                ? "Others"
                : "",
              po_box: vendor.po_box || "",
              phone_number: vendor.phone_number || "",
              email: vendor.email || "",
              vat_no: vendor.vat_no || "",
              status: vendor.status,
            });
          } else {
            alertRef.current.triggerAlert(
              JSON.stringify({
                message: "vendor not found",
                messageType: "error",
              })
            );
          }
        } catch (error) {
          alertRef.current.triggerAlert(
            JSON.stringify({ message: error.msg, messageType: "error" })
          );
        }
      };

      fetchvendor();
    }
  }, [vendorid]);

  return (
    <Layout>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid className="employee-form">
        <AlertMessage ref={alertRef} />
        <Grid container spacing={3}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Supplier Creation
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Suppliers</Typography>
              <Typography color="text.primary">Creation</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid
            textAlign="right"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <LoadingButton
              size="medium"
              variant="contained"
              color="secondary"
              className="button_first"
              href="/vendor/list"
            >
              List Suppliers
            </LoadingButton>
            <LoadingButton
              size="medium"
              variant="contained"
              color="primary"
              onClick={() => navigate(-1)}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </LoadingButton>
          </Grid>
        </Grid>

        <form className="form_design" onSubmit={formik.handleSubmit}>
          {/* Company Name & Address */}
          <h3>Supplier Name & Address</h3>
          <Grid container spacing={2}>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="name"
                label="Supplier Name"
                fullWidth
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
              />
            </Grid>

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="company_name"
                label="Company Name"
                fullWidth
                value={formik.values.company_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.company_name &&
                  Boolean(formik.errors.company_name)
                }
                helperText={
                  formik.touched.company_name && formik.errors.company_name
                }
              />
            </Grid>

            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="address"
                label="Street Address"
                fullWidth
                value={formik.values.address}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>
            <Grid
              item
              className="form-lable"
              xs={12}
              lg={formik.values.emirateOption === "Others" ? 3 : 6}
              md={6}
            >
              <FormControl fullWidth>
                <TextField
                  name="emirateOption"
                  select
                  label="Emirate"
                  fullWidth
                  value={formik.values.emirateOption}
                  onChange={(e) => {
                    const value = e.target.value;
                    formik.setFieldValue("emirateOption", value);
                    formik.setFieldValue(
                      "emirate",
                      value === "Others" ? "" : value
                    );
                  }}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                  helperText={formik.touched.emirate && formik.errors.emirate}
                >
                  {emirates.map((emirate) => (
                    <MenuItem key={emirate.id} value={emirate.id}>
                      {emirate.name}
                    </MenuItem>
                  ))}
                  <MenuItem value="Others">Others</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            {formik.values.emirateOption === "Others" && (
              <Grid item className="form-lable" xs={12} lg={3} md={6}>
                <TextField
                  name="emirate"
                  label="Please specify Emirate/City/Country"
                  placeholder="Enter emirate, city or country"
                  fullWidth
                  value={formik.values.emirate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.emirate && Boolean(formik.errors.emirate)
                  }
                  helperText={formik.touched.emirate && formik.errors.emirate}
                />
              </Grid>
            )}
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="po_box"
                type="number"
                label="P.O.Box"
                fullWidth
                value={formik.values.po_box}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.po_box && Boolean(formik.errors.po_box)}
                helperText={formik.touched.po_box && formik.errors.po_box}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="phone_number"
                type="text"
                label="Phone Number"
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                }}
                value={formik.values.phone_number}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.phone_number &&
                  Boolean(formik.errors.phone_number)
                }
                helperText={
                  formik.touched.phone_number && formik.errors.phone_number
                }
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="email"
                type="text"
                label="Vendor Email Id"
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item className="form-lable" xs={12} lg={6} md={6}>
              <TextField
                name="vat_no"
                label="VAT No."
                fullWidth
                inputProps={{ inputMode: "numeric", maxLength: 15 }}
                value={formik.values.vat_no}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.vat_no && Boolean(formik.errors.vat_no)}
                helperText={formik.touched.vat_no && formik.errors.vat_no}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                name="status"
                value={formik.values.status}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.status && Boolean(formik.errors.status)}
                helperText={formik.touched.status && formik.errors.status}
              >
                {status?.map((option) => (
                  <MenuItem value={option.id}>{option.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>

          {/* Button */}
          <Grid container className="submit-button" alignItems="center">
            <Grid
              item
              xs={12}
              lg={12}
              md={12}
              display="flex"
              justifyContent="center"
            >
              <LoadingButton
                size="large"
                variant="contained"
                color="primary"
                type="submit"
                disabled={formik.isSubmitting}
              >
                Submit
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Layout>
  );
};

export default Vendor1Add;
